import { DatePipe } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { BaseComponent } from '@premotec/pmt-essentials';

import { HeaderStateService } from '../../../../main/headers/services/header-state.service';
import { DateHelper } from '../../../../shared/helpers/date.helper';
import { SharedToasterService } from '../../../../shared/services/shared-toaster.service';
import { AssignmentScheduleOptionAnswerEnum } from '../../enums/assignment-schedule-option-answer.enum';
import { AssignmentScheduleChildModel } from '../../models/assignment-schedule-child.model';
import { AssignmentScheduleOptionModel } from '../../models/assignment-schedule-option.model';
import { AssignmentScheduleUpdateAnswerPostModel } from '../../models/assignment-schedule-update-answer-post.model';
import { AssignmentScheduleService } from '../../services/assignment-schedule.service';
import { AssignmentScheduleStateService } from '../../services/assignment-schedule-state.service';

@Component({
    selector: 'app-assignment-schedule-answer-option',
    templateUrl: './assignment-schedule-answer-option.component.html',
    styleUrl: './assignment-schedule-answer-option.component.scss'
})
export class AssignmentScheduleAnswerOptionComponent extends BaseComponent implements OnInit {
    @Input({ required: true }) public option!: AssignmentScheduleOptionModel;
    @Input({ required: true }) public assignmentScheduleId!: string;

    protected dateHelper = inject(DateHelper)
    protected assignmentScheduleService = inject(AssignmentScheduleService);
    protected assignmentScheduleStateService = inject(AssignmentScheduleStateService);

    protected showAll: boolean = false;
    protected isLoading: boolean = false;
    protected assignmentScheduleOptionAnswerEnum = AssignmentScheduleOptionAnswerEnum;

    private minParticipantsCount: number = 3;


    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private headerStateService: HeaderStateService,
        private sharedToasterService: SharedToasterService,
        private translocoService: TranslocoService
    ) {
        super();
    }

    ngOnInit(): void {
    }


    protected get IsOnSameDay(): boolean {
        return this.dateHelper.IsOnSameDay(this.option.from, this.option.to);
    }

    protected setAnswer(answer: AssignmentScheduleOptionAnswerEnum) {
        if (this.option.answer == answer) return;
        this.updateAnswer(answer);
    }

    protected get isYes(): boolean {
        return this.option.answer == AssignmentScheduleOptionAnswerEnum.yes;
    }

    protected get isMaybe(): boolean {
        return this.option.answer == AssignmentScheduleOptionAnswerEnum.maybe;
    }

    protected get isNo(): boolean {
        return this.option.answer == AssignmentScheduleOptionAnswerEnum.no;
    }

    protected get filteredChildren(): AssignmentScheduleChildModel[] {

        let children = this.option.children;

        if (!this.showAll) {
            children = children.slice(0, this.minParticipantsCount);
        }

        children = children.sort((a, b) => {
            const dateA = new Date(a.from!).getTime();
            const dateB = new Date(b.from!).getTime();

            // Sort start time first
            const startDateComparison = dateA - dateB;
            if (startDateComparison !== 0) {
                return startDateComparison;
            }

            // If start dates equal sort by end time
            const endA = new Date(a.to!).getTime();
            const endB = new Date(b.to!).getTime();
            return endA - endB;
        });

        return children;
    }

    protected get childrenCount(): number {
        return this.option.children.length;
    }

    protected get formattedDate(): string {
        const optionFrom = this.option.from;
        const optionTo = this.option.to;
        const isSameDay = this.dateHelper.IsOnSameDay(this.option.from, this.option.to);

        let formatedFrom = this.formatDate(optionFrom, 'dd.MM.yy HH:mm');
        let formatedTo = isSameDay ? this.formatDate(optionTo, 'HH:mm') : this.formatDate(optionTo, 'dd.MM.yy HH:mm');

        return `${formatedFrom} - ${formatedTo}`;
    }

    protected get weekdayRange(): number[] {
        return this.assignmentScheduleService.getWeekdayRange(this.option);
    }

    protected getWeekdayFromIndex(index: number): string {
        return this.translocoService.translate(`shared.weekdays.short.${index}`);
    }

    private formatDate(date: Date | string, format: string): string {
        const datePipe = new DatePipe('de-CH');
        return datePipe.transform(date, format)!;
    }

    private updateAnswer(answer: AssignmentScheduleOptionAnswerEnum): void {
        if (this.assignmentScheduleStateService.isFullyDisabled.value) return;
        if (this.isLoading) return;
        this.isLoading = true;

        var model: AssignmentScheduleUpdateAnswerPostModel = {
            eventLessonId: this.option.eventLessonId,
            answer: answer
        }

        this.assignmentScheduleService.changeAssignmentScheduleAnswerById(this.assignmentScheduleId, model)
            .subscribe({
                next: () => {
                    this.option.answer = answer
                    this.isLoading = false;
                },
                error: () => {
                    this.isLoading = false;
                }
            });
    }
}
